/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { WaypointType } from 'src/graphql/generated/graphql.generated';
import { AddressRecord, AddressProps } from 'src/store/generated/address-record.generated';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface RequestLocationProps {
  id: string;
  name: string;
  abbreviation: string;
  type: WaypointType;
  locationId: string | null;
  address: AddressProps;
  timezoneCd: string | null;
  isRotorWingLz: boolean | null;
  medicalFacilityId: string | null;
  roomNumber: string | null;
  medicalFacilityUnitId: string | null;
  notes: string | null;
  groundContact: string | null;
  estimatedRendezvousTimeInMinutes: number | null;
  estimatedGroundTimeInMinutes: number | null;
  fboId: string | null;
  physicianId: string | null;
  diversionReasonId: string | null;
  diversionReasonOtherNotes: string | null;
  isAutoAccepted: boolean | null;
  dateConfirmed: Date | null;
  confirmedByUserId: string | null;
  unConfirmed: boolean | null;
  physicianName: string | null;
  requestId: string;
  companyId: string;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class RequestLocationRecord extends Record<RequestLocationProps>({
  id: null as any,
  name: null as any,
  abbreviation: null as any,
  type: null as any,
  locationId: null as any,
  address: null as any,
  timezoneCd: null as any,
  isRotorWingLz: null as any,
  medicalFacilityId: null as any,
  roomNumber: null as any,
  medicalFacilityUnitId: null as any,
  notes: null as any,
  groundContact: null as any,
  estimatedRendezvousTimeInMinutes: null as any,
  estimatedGroundTimeInMinutes: null as any,
  fboId: null as any,
  physicianId: null as any,
  diversionReasonId: null as any,
  diversionReasonOtherNotes: null as any,
  isAutoAccepted: null as any,
  dateConfirmed: null as any,
  confirmedByUserId: null as any,
  unConfirmed: null as any,
  physicianName: null as any,
  requestId: null as any,
  companyId: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    name: string;
    abbreviation: string;
    type: WaypointType;
    locationId: string | null;
    address: any;
    timezoneCd: string | null;
    isRotorWingLz: boolean | null;
    medicalFacilityId: string | null;
    roomNumber: string | null;
    medicalFacilityUnitId: string | null;
    notes: string | null;
    groundContact: string | null;
    estimatedRendezvousTimeInMinutes: number | null;
    estimatedGroundTimeInMinutes: number | null;
    fboId: string | null;
    physicianId: string | null;
    diversionReasonId: string | null;
    diversionReasonOtherNotes: string | null;
    isAutoAccepted: boolean | null;
    dateConfirmed: string | number | Date | null;
    confirmedByUserId: string | null;
    unConfirmed: boolean | null;
    physicianName: string | null;
    requestId: string;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): RequestLocationRecord {
    if (json == null) {
      throw new Error('RequestLocationRecord.fromJS: parameter cannot be null');
    }
    return new RequestLocationRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'RequestLocationJson.id',
      ),
      name: getFromOrThrow(json, (j) => j.name, 'RequestLocationJson.name'),
      abbreviation: getFromOrThrow(json, (j) => j.abbreviation, 'RequestLocationJson.abbreviation'),
      type: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'type', WaypointType.Post),
        'RequestLocationJson.type',
      ),
      locationId: json.locationId,
      address: new AddressRecord(
        getFromOrThrow(json, (j) => j.address, 'RequestLocationJson.address'),
      ),
      timezoneCd: json.timezoneCd,
      isRotorWingLz: json.isRotorWingLz,
      medicalFacilityId: json.medicalFacilityId,
      roomNumber: json.roomNumber,
      medicalFacilityUnitId: json.medicalFacilityUnitId,
      notes: json.notes,
      groundContact: json.groundContact,
      estimatedRendezvousTimeInMinutes: json.estimatedRendezvousTimeInMinutes,
      estimatedGroundTimeInMinutes: json.estimatedGroundTimeInMinutes,
      fboId: json.fboId,
      physicianId: json.physicianId,
      diversionReasonId: json.diversionReasonId,
      diversionReasonOtherNotes: json.diversionReasonOtherNotes,
      isAutoAccepted: json.isAutoAccepted,
      dateConfirmed: json.dateConfirmed != null ? new Date(json.dateConfirmed) : null,
      confirmedByUserId: json.confirmedByUserId,
      unConfirmed: json.unConfirmed,
      physicianName: json.physicianName,
      requestId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'requestId', '00000000-0000-0000-0000-000000000000'),
        'RequestLocationJson.requestId',
      ),
      companyId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
        'RequestLocationJson.companyId',
      ),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    name: string;
    abbreviation: string;
    type: WaypointType;
    locationId: string | null;
    address: any;
    timezoneCd: string | null;
    isRotorWingLz: boolean | null;
    medicalFacilityId: string | null;
    roomNumber: string | null;
    medicalFacilityUnitId: string | null;
    notes: string | null;
    groundContact: string | null;
    estimatedRendezvousTimeInMinutes: number | null;
    estimatedGroundTimeInMinutes: number | null;
    fboId: string | null;
    physicianId: string | null;
    diversionReasonId: string | null;
    diversionReasonOtherNotes: string | null;
    isAutoAccepted: boolean | null;
    dateConfirmed: string | number | Date | null;
    confirmedByUserId: string | null;
    unConfirmed: boolean | null;
    physicianName: string | null;
    requestId: string;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [RequestLocationRecord, Error[]] {
    if (json == null) {
      throw new Error('RequestLocationRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new RequestLocationRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'RequestLocationJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      name: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.name, 'RequestLocationJson.name'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      abbreviation: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.abbreviation, 'RequestLocationJson.abbreviation'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      type: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'type', WaypointType.Post),
            'RequestLocationJson.type',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      locationId: tryOrDefault(
        () => json.locationId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      address: tryOrDefault(
        () =>
          new AddressRecord(getFromOrThrow(json, (j) => j.address, 'RequestLocationJson.address')),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      timezoneCd: tryOrDefault(
        () => json.timezoneCd,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      isRotorWingLz: tryOrDefault(
        () => json.isRotorWingLz,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      medicalFacilityId: tryOrDefault(
        () => json.medicalFacilityId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      roomNumber: tryOrDefault(
        () => json.roomNumber,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      medicalFacilityUnitId: tryOrDefault(
        () => json.medicalFacilityUnitId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      notes: tryOrDefault(
        () => json.notes,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      groundContact: tryOrDefault(
        () => json.groundContact,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      estimatedRendezvousTimeInMinutes: tryOrDefault(
        () => json.estimatedRendezvousTimeInMinutes,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      estimatedGroundTimeInMinutes: tryOrDefault(
        () => json.estimatedGroundTimeInMinutes,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      fboId: tryOrDefault(
        () => json.fboId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      physicianId: tryOrDefault(
        () => json.physicianId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      diversionReasonId: tryOrDefault(
        () => json.diversionReasonId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      diversionReasonOtherNotes: tryOrDefault(
        () => json.diversionReasonOtherNotes,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      isAutoAccepted: tryOrDefault(
        () => json.isAutoAccepted,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      dateConfirmed: tryOrDefault(
        () => (json.dateConfirmed != null ? new Date(json.dateConfirmed) : null),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      confirmedByUserId: tryOrDefault(
        () => json.confirmedByUserId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      unConfirmed: tryOrDefault(
        () => json.unConfirmed,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      physicianName: tryOrDefault(
        () => json.physicianName,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      requestId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'requestId', '00000000-0000-0000-0000-000000000000'),
            'RequestLocationJson.requestId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      companyId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
            'RequestLocationJson.companyId',
          ),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: RequestLocationProps) {
    super(props);
    if (props == null) {
      throw new Error('RequestLocationRecord constructor parameter cannot be null');
    }
  }
}

export abstract class RequestLocationViewModelBase implements RequestLocationProps {
  protected readonly _props: RequestLocationProps;
  private _json?: RequestLocationProps;

  get id(): string {
    return this._props.id;
  }
  get name(): string {
    return this._props.name;
  }
  get abbreviation(): string {
    return this._props.abbreviation;
  }
  get type(): WaypointType {
    return this._props.type;
  }
  get locationId(): string | null {
    return this._props.locationId;
  }
  get address(): AddressProps {
    return this._props.address;
  }
  get timezoneCd(): string | null {
    return this._props.timezoneCd;
  }
  get isRotorWingLz(): boolean | null {
    return this._props.isRotorWingLz;
  }
  get medicalFacilityId(): string | null {
    return this._props.medicalFacilityId;
  }
  get roomNumber(): string | null {
    return this._props.roomNumber;
  }
  get medicalFacilityUnitId(): string | null {
    return this._props.medicalFacilityUnitId;
  }
  get notes(): string | null {
    return this._props.notes;
  }
  get groundContact(): string | null {
    return this._props.groundContact;
  }
  get estimatedRendezvousTimeInMinutes(): number | null {
    return this._props.estimatedRendezvousTimeInMinutes;
  }
  get estimatedGroundTimeInMinutes(): number | null {
    return this._props.estimatedGroundTimeInMinutes;
  }
  get fboId(): string | null {
    return this._props.fboId;
  }
  get physicianId(): string | null {
    return this._props.physicianId;
  }
  get diversionReasonId(): string | null {
    return this._props.diversionReasonId;
  }
  get diversionReasonOtherNotes(): string | null {
    return this._props.diversionReasonOtherNotes;
  }
  get isAutoAccepted(): boolean | null {
    return this._props.isAutoAccepted;
  }
  get dateConfirmed(): Date | null {
    return this._props.dateConfirmed;
  }
  get confirmedByUserId(): string | null {
    return this._props.confirmedByUserId;
  }
  get unConfirmed(): boolean | null {
    return this._props.unConfirmed;
  }
  get physicianName(): string | null {
    return this._props.physicianName;
  }
  get requestId(): string {
    return this._props.requestId;
  }
  get companyId(): string {
    return this._props.companyId;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  public get isDropoffTeam() {
    return this._props.type === WaypointType.DropoffTeam;
  }
  public get isEnRouteDiversion() {
    return this._props.type === WaypointType.EnRouteDiversion;
  }
  public get isFuelBurn() {
    return this._props.type === WaypointType.FuelBurn;
  }
  public get isFuelLocation() {
    return this._props.type === WaypointType.FuelLocation;
  }
  public get isOther() {
    return this._props.type === WaypointType.Other;
  }
  public get isPatientDropoffRendezvous() {
    return this._props.type === WaypointType.PatientDropoffRendezvous;
  }
  public get isPatientPickupRendezvous() {
    return this._props.type === WaypointType.PatientPickupRendezvous;
  }
  public get isPickupTeam() {
    return this._props.type === WaypointType.PickupTeam;
  }
  public get isPost() {
    return this._props.type === WaypointType.Post;
  }
  public get isPublicRelationsSite() {
    return this._props.type === WaypointType.PublicRelationsSite;
  }
  public get isReceivingFacility() {
    return this._props.type === WaypointType.ReceivingFacility;
  }
  public get isRendezvousPoint() {
    return this._props.type === WaypointType.RendezvousPoint;
  }
  public get isSceneLocation() {
    return this._props.type === WaypointType.SceneLocation;
  }
  public get isSendingFacility() {
    return this._props.type === WaypointType.SendingFacility;
  }

  public isAnyType(...waypointType: WaypointType[]) {
    return waypointType.includes(this._props.type);
  }

  constructor(props: RequestLocationProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        name: this._props.name,
        abbreviation: this._props.abbreviation,
        type: this._props.type,
        locationId: this._props.locationId,
        address: this._props.address,
        timezoneCd: this._props.timezoneCd,
        isRotorWingLz: this._props.isRotorWingLz,
        medicalFacilityId: this._props.medicalFacilityId,
        roomNumber: this._props.roomNumber,
        medicalFacilityUnitId: this._props.medicalFacilityUnitId,
        notes: this._props.notes,
        groundContact: this._props.groundContact,
        estimatedRendezvousTimeInMinutes: this._props.estimatedRendezvousTimeInMinutes,
        estimatedGroundTimeInMinutes: this._props.estimatedGroundTimeInMinutes,
        fboId: this._props.fboId,
        physicianId: this._props.physicianId,
        diversionReasonId: this._props.diversionReasonId,
        diversionReasonOtherNotes: this._props.diversionReasonOtherNotes,
        isAutoAccepted: this._props.isAutoAccepted,
        dateConfirmed: this._props.dateConfirmed,
        confirmedByUserId: this._props.confirmedByUserId,
        unConfirmed: this._props.unConfirmed,
        physicianName: this._props.physicianName,
        requestId: this._props.requestId,
        companyId: this._props.companyId,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllRequestLocationEntities = (state: AppState) =>
  state.entityStores.requestLocation.entities;

export const selectRequestLocationById = (id: string) => (state: AppState) =>
  state.entityStores.requestLocation.entities.get(id);

export const selectRequestLocationMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.requestLocation.meta.get(id);

export const selectRequestLocationAndMetadataById = (id: string) =>
  createSelector(
    selectRequestLocationById(id),
    selectRequestLocationMetadataById(id),
    (requestLocation, meta) => ({ requestLocation, meta }),
  );

export function selectRequestLocationEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllRequestLocationEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, RequestLocationRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectRequestLocationEntitiesByName(name: string) {
  return createSelector(selectAllRequestLocationEntities, (entities) =>
    entities.filter((e) => e.name === name).valueSeq(),
  );
}

export function selectRequestLocationEntitiesByAbbreviation(abbreviation: string) {
  return createSelector(selectAllRequestLocationEntities, (entities) =>
    entities.filter((e) => e.abbreviation === abbreviation).valueSeq(),
  );
}

export function selectRequestLocationEntitiesByType(type: WaypointType) {
  return createSelector(selectAllRequestLocationEntities, (entities) =>
    entities.filter((e) => e.type === type).valueSeq(),
  );
}

export function selectRequestLocationEntitiesByLocationId(locationId: string) {
  return createSelector(selectAllRequestLocationEntities, (entities) =>
    entities.filter((e) => e.locationId === locationId).valueSeq(),
  );
}

export function selectRequestLocationEntitiesByTimezoneCd(timezoneCd: string) {
  return createSelector(selectAllRequestLocationEntities, (entities) =>
    entities.filter((e) => e.timezoneCd === timezoneCd).valueSeq(),
  );
}

export function selectRequestLocationEntitiesByIsRotorWingLz(isRotorWingLz: boolean) {
  return createSelector(selectAllRequestLocationEntities, (entities) =>
    entities.filter((e) => e.isRotorWingLz === isRotorWingLz).valueSeq(),
  );
}

export function selectRequestLocationEntitiesByMedicalFacilityId(medicalFacilityId: string) {
  return createSelector(selectAllRequestLocationEntities, (entities) =>
    entities.filter((e) => e.medicalFacilityId === medicalFacilityId).valueSeq(),
  );
}

export function selectRequestLocationEntitiesByRoomNumber(roomNumber: string) {
  return createSelector(selectAllRequestLocationEntities, (entities) =>
    entities.filter((e) => e.roomNumber === roomNumber).valueSeq(),
  );
}

export function selectRequestLocationEntitiesByMedicalFacilityUnitId(
  medicalFacilityUnitId: string,
) {
  return createSelector(selectAllRequestLocationEntities, (entities) =>
    entities.filter((e) => e.medicalFacilityUnitId === medicalFacilityUnitId).valueSeq(),
  );
}

export function selectRequestLocationEntitiesByNotes(notes: string) {
  return createSelector(selectAllRequestLocationEntities, (entities) =>
    entities.filter((e) => e.notes === notes).valueSeq(),
  );
}

export function selectRequestLocationEntitiesByGroundContact(groundContact: string) {
  return createSelector(selectAllRequestLocationEntities, (entities) =>
    entities.filter((e) => e.groundContact === groundContact).valueSeq(),
  );
}

export function selectRequestLocationEntitiesByEstimatedRendezvousTimeInMinutes(
  estimatedRendezvousTimeInMinutes: number,
) {
  return createSelector(selectAllRequestLocationEntities, (entities) =>
    entities
      .filter((e) => e.estimatedRendezvousTimeInMinutes === estimatedRendezvousTimeInMinutes)
      .valueSeq(),
  );
}

export function selectRequestLocationEntitiesByEstimatedGroundTimeInMinutes(
  estimatedGroundTimeInMinutes: number,
) {
  return createSelector(selectAllRequestLocationEntities, (entities) =>
    entities
      .filter((e) => e.estimatedGroundTimeInMinutes === estimatedGroundTimeInMinutes)
      .valueSeq(),
  );
}

export function selectRequestLocationEntitiesByFboId(fboId: string) {
  return createSelector(selectAllRequestLocationEntities, (entities) =>
    entities.filter((e) => e.fboId === fboId).valueSeq(),
  );
}

export function selectRequestLocationEntitiesByPhysicianId(physicianId: string) {
  return createSelector(selectAllRequestLocationEntities, (entities) =>
    entities.filter((e) => e.physicianId === physicianId).valueSeq(),
  );
}

export function selectRequestLocationEntitiesByDiversionReasonId(diversionReasonId: string) {
  return createSelector(selectAllRequestLocationEntities, (entities) =>
    entities.filter((e) => e.diversionReasonId === diversionReasonId).valueSeq(),
  );
}

export function selectRequestLocationEntitiesByDiversionReasonOtherNotes(
  diversionReasonOtherNotes: string,
) {
  return createSelector(selectAllRequestLocationEntities, (entities) =>
    entities.filter((e) => e.diversionReasonOtherNotes === diversionReasonOtherNotes).valueSeq(),
  );
}

export function selectRequestLocationEntitiesByIsAutoAccepted(isAutoAccepted: boolean) {
  return createSelector(selectAllRequestLocationEntities, (entities) =>
    entities.filter((e) => e.isAutoAccepted === isAutoAccepted).valueSeq(),
  );
}

export function selectRequestLocationEntitiesByDateConfirmed(dateConfirmed: Date) {
  return createSelector(selectAllRequestLocationEntities, (entities) =>
    entities.filter((e) => e.dateConfirmed === dateConfirmed).valueSeq(),
  );
}

export function selectRequestLocationEntitiesByConfirmedByUserId(confirmedByUserId: string) {
  return createSelector(selectAllRequestLocationEntities, (entities) =>
    entities.filter((e) => e.confirmedByUserId === confirmedByUserId).valueSeq(),
  );
}

export function selectRequestLocationEntitiesByUnConfirmed(unConfirmed: boolean) {
  return createSelector(selectAllRequestLocationEntities, (entities) =>
    entities.filter((e) => e.unConfirmed === unConfirmed).valueSeq(),
  );
}

export function selectRequestLocationEntitiesByPhysicianName(physicianName: string) {
  return createSelector(selectAllRequestLocationEntities, (entities) =>
    entities.filter((e) => e.physicianName === physicianName).valueSeq(),
  );
}

export function selectRequestLocationEntitiesByRequestId(requestId: string) {
  return createSelector(selectAllRequestLocationEntities, (entities) =>
    entities.filter((e) => e.requestId === requestId).valueSeq(),
  );
}

export function selectRequestLocationEntitiesByCompanyId(companyId: string) {
  return createSelector(selectAllRequestLocationEntities, (entities) =>
    entities.filter((e) => e.companyId === companyId).valueSeq(),
  );
}
//#endregion Selectors
